import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listMyOrders, getMyOrder } from "@/lib/account/account.functions";
import { useEffect, useMemo, useState } from "react";
import {
  Package, Truck, Clock, XCircle, RotateCcw, ChevronDown, Search, MapPin, CreditCard,
  ExternalLink, Loader2, ArrowRight, Copy, CheckCircle2, ShoppingBag, Hash, CalendarDays, Star,
} from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/lib/cart/cart-store";
import { ReviewDialog } from "@/components/account/ReviewDialog";


export const Route = createFileRoute("/_account/account/orders")({
  component: OrdersPage,
});

const filters = [
  { id: "all", label: "All" },
  { id: "pending_payment", label: "Pending payment" },
  { id: "paid", label: "Paid" },
  { id: "started", label: "In production" },
  { id: "shipped", label: "Shipped" },
  { id: "cancelled", label: "Cancelled" },
  { id: "returned", label: "Returned" },
] as const;

const meta: Record<string, { label: string; icon: any; tone: string; dot: string }> = {
  pending_payment: { label: "Pending payment", icon: Clock, tone: "bg-slate-100 text-slate-700", dot: "bg-slate-400" },
  paid: { label: "Paid", icon: CreditCard, tone: "bg-sky-100 text-sky-900", dot: "bg-sky-500" },
  started: { label: "In production", icon: Package, tone: "bg-amber-100 text-amber-900", dot: "bg-amber-500" },
  shipped: { label: "Shipped", icon: Truck, tone: "bg-emerald-100 text-emerald-900", dot: "bg-emerald-500" },
  cancelled: { label: "Cancelled", icon: XCircle, tone: "bg-zinc-100 text-zinc-700", dot: "bg-zinc-400" },
  returned: { label: "Returned", icon: RotateCcw, tone: "bg-rose-100 text-rose-900", dot: "bg-rose-500" },
};

function metaFor(status: string) {
  return (
    meta[status] ?? {
      label: String(status ?? "").replace(/_/g, " ") || "Unknown",
      icon: Clock,
      tone: "bg-slate-100 text-slate-700",
      dot: "bg-slate-400",
    }
  );
}

const pipeline = ["paid", "started", "shipped"] as const;

function eur(v: number | string | null | undefined) {
  return `${Number(v ?? 0).toFixed(2)} €`;
}

function countryFlag(iso?: string | null) {
  if (!iso || iso.length !== 2) return "";
  const A = 0x1f1e6;
  return String.fromCodePoint(A + iso.toUpperCase().charCodeAt(0) - 65, A + iso.toUpperCase().charCodeAt(1) - 65);
}

function carrierUrl(c?: string | null, n?: string | null) {
  if (!c || !n) return null;
  const k = c.toLowerCase();
  if (k.includes("dhl")) return `https://www.dhl.com/global-en/home/tracking.html?tracking-id=${encodeURIComponent(n)}`;
  if (k.includes("ups")) return `https://www.ups.com/track?tracknum=${encodeURIComponent(n)}`;
  if (k.includes("fedex")) return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}`;
  if (k.includes("speedy")) return `https://www.speedy.bg/en/track-shipment?shipmentNumber=${encodeURIComponent(n)}`;
  if (k.includes("econt")) return `https://www.econt.com/services/track-shipment/${encodeURIComponent(n)}`;
  return null;
}

function timeAgo(iso?: string | null) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const d = Math.floor(diff / 86400000);
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d} days ago`;
  const m = Math.floor(d / 30);
  return m < 12 ? `${m} month${m === 1 ? "" : "s"} ago` : `${Math.floor(m / 12)}y ago`;
}

function CopyChip({ text, label }: { text: string; label: string }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(text);
        setOk(true);
        toast.success(`${label} copied`);
        setTimeout(() => setOk(false), 1400);
      }}
      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-50 active:scale-95"
    >
      {ok ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
      {label}
    </button>
  );
}

function OrdersPage() {
  const [status, setStatus] = useState<string>("all");
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const list = useServerFn(listMyOrders);
  const ordersQ = useQuery({
    queryKey: ["account", "orders", status, debounced],
    queryFn: () => list({ data: { status, q: debounced || undefined } }),
  });
  const orders = (ordersQ.data ?? []) as any[];

  const summary = useMemo(() => {
    const spent = orders
      .filter((o) => !["cancelled", "returned", "pending_payment"].includes(o.status))
      .reduce((s, o) => s + Number(o.total_eur ?? 0), 0);
    const active = orders.filter((o) => ["paid", "started", "shipped"].includes(o.status)).length;
    return { count: orders.length, spent, active };
  }, [orders]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-slate-900">Your orders</h1>
          <p className="text-sm text-slate-500">Track every order from payment to delivery.</p>
        </div>
        {orders.length > 0 && (
          <div className="flex gap-2 text-xs">
            <Stat label="Orders" value={String(summary.count)} />
            <Stat label="Active" value={String(summary.active)} />
            <Stat label="Spent" value={eur(summary.spent)} />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-1">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setStatus(f.id)}
              className={`rounded-full px-3 py-1.5 text-sm font-medium transition active:scale-95 ${
                status === f.id ? "bg-slate-900 text-white shadow-sm" : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search order # or tracking…"
            className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-[#2563EB] sm:w-64"
          />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white">
        {ordersQ.isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" style={{ animationDelay: `${i * 120}ms` }} />
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="p-10 text-center">
            <Package className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm font-semibold text-slate-900">No orders found</p>
            <p className="text-sm text-slate-500">Try a different filter.</p>
            <Link
              to="/shop"
              className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#1d4ed8]"
            >
              <ShoppingBag className="h-4 w-4" /> Browse parts
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {orders.map((o, i) => (
              <OrderRow
                key={o.id}
                order={o}
                index={i}
                open={openId === o.id}
                onToggle={() => setOpenId((cur) => (cur === o.id ? null : o.id))}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-1.5">
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-sm font-bold text-slate-900">{value}</p>
    </div>
  );
}

function OrderRow({
  order: o,
  open,
  onToggle,
  index,
}: {
  order: any;
  open: boolean;
  onToggle: () => void;
  index: number;
}) {
  const m = metaFor(o.status);
  const Icon = m.icon;
  const items = (o.order_items ?? []).reduce((s: number, i: any) => s + i.quantity, 0);

  return (
    <li className="animate-fade-up opacity-0" style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}>
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        aria-expanded={open}
        className={`flex cursor-pointer items-center gap-3 p-4 transition ${open ? "bg-slate-50" : "hover:bg-slate-50"}`}
      >
        <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-full transition ${m.tone} ${open ? "scale-110" : ""}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">#{o.order_number}</p>
          <p className="truncate text-xs text-slate-500">
            {items} item{items === 1 ? "" : "s"} · {countryFlag(o.country)} {o.country}
            {(o.paid_at || o.created_at) && ` · ${new Date(o.paid_at ?? o.created_at).toLocaleDateString()}`}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-sm font-bold text-slate-900">{eur(o.total_eur)}</p>
          <p className="text-xs text-slate-500">{m.label}</p>
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-300 ${open ? "rotate-180 text-[#2563EB]" : ""}`}
        />
      </div>

      <div
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          {open && <OrderDetailPanel order={o} />}
        </div>
      </div>
    </li>
  );
}

function OrderDetailPanel({ order: o }: { order: any }) {
  const get = useServerFn(getMyOrder);
  const q = useQuery({
    queryKey: ["account", "order", o.id],
    queryFn: () => get({ data: { id: o.id } }),
    staleTime: 30_000,
  });
  const add = useCart((s) => s.add);
  const navigate = useNavigate();
  const [reviewTarget, setReviewTarget] = useState<
    { productId: string; productName: string; imageUrl?: string | null } | null
  >(null);


  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50/60 p-6 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading order details…
      </div>
    );
  }
  if (!q.data) {
    return (
      <div className="border-t border-slate-100 bg-slate-50/60 p-6 text-sm text-slate-500">
        Couldn’t load details.{" "}
        <button onClick={() => q.refetch()} className="font-semibold text-[#2563EB] underline">Retry</button>
      </div>
    );
  }

  const { order, items, history } = q.data as any;
  const status: string = order.status;
  const isSide = status === "cancelled" || status === "returned";
  const currentStep = pipeline.indexOf(status as any);
  const trackUrl = carrierUrl(order.tracking_carrier, order.tracking_number);
  const flag = countryFlag(order.country);

  const reorder = () => {
    let added = 0;
    for (const it of items as any[]) {
      if (!it.variant_id || !it.product_slug) continue;
      add({
        productId: it.product_id ?? it.variant_id,
        variantId: it.variant_id,
        sku: it.product_sku,
        slug: it.product_slug,
        title: it.product_name,
        image: it.image_url,
        unitPriceCents: Math.round(Number(it.unit_price_eur) * 100),
        qty: it.quantity,
        currency: "EUR",
      } as any);
      added++;
    }
    if (!added) {
      toast.error("No re-orderable items in this order");
      return;
    }
    toast.success(`Added ${added} item${added === 1 ? "" : "s"} to your cart`);
    navigate({ to: "/cart" as never });
  };

  const delay = (i: number) => ({ animationDelay: `${i * 60}ms` } as const);

  return (
    <div className="border-t border-slate-100 bg-gradient-to-b from-slate-50 to-white p-4 sm:p-5">
      {/* Progress */}
      <div className="animate-fade-up opacity-0 rounded-xl border border-slate-200 bg-white p-4" style={delay(0)}>
        {isSide ? (
          <div className={`flex items-center gap-3 rounded-lg p-3 ${metaFor(status).tone}`}>
            <RotateCcw className="h-4 w-4" />
            <p className="text-sm font-semibold">
              {metaFor(status).label}
              {order.cancelled_at && ` · ${new Date(order.cancelled_at).toLocaleDateString()}`}
              {order.returned_at && ` · ${new Date(order.returned_at).toLocaleDateString()}`}
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              {pipeline.map((s, idx) => {
                const sm = meta[s];
                const SIcon = sm.icon;
                const done = idx <= currentStep;
                const ts = s === "paid" ? order.paid_at : s === "started" ? order.started_at : order.shipped_at;
                return (
                  <div key={s} className="flex min-w-0 flex-1 flex-col items-center text-center">
                    <div className="flex w-full items-center">
                      <span className={`h-0.5 flex-1 ${idx === 0 ? "bg-transparent" : idx <= currentStep ? "bg-[#2563EB]" : "bg-slate-200"}`} />
                      <span
                        className={`grid h-8 w-8 shrink-0 place-items-center rounded-full transition-all duration-500 ${
                          done ? "bg-[#2563EB] text-white shadow-[0_0_0_4px_rgba(37,99,235,0.15)]" : "bg-slate-100 text-slate-400"
                        }`}
                      >
                        {idx < currentStep ? <CheckCircle2 className="h-4 w-4" /> : <SIcon className="h-4 w-4" />}
                      </span>
                      <span className={`h-0.5 flex-1 ${idx === pipeline.length - 1 ? "bg-transparent" : idx < currentStep ? "bg-[#2563EB]" : "bg-slate-200"}`} />
                    </div>
                    <p className={`mt-1.5 truncate text-[11px] font-semibold ${done ? "text-slate-900" : "text-slate-400"}`}>{sm.label}</p>
                    <p className="text-[10px] text-slate-400">{ts ? new Date(ts).toLocaleDateString() : "—"}</p>
                  </div>
                );
              })}
            </div>
            {order.tracking_number && (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Tracking</p>
                  <p className="truncate font-mono text-xs font-semibold text-emerald-900">
                    {order.tracking_carrier} · {order.tracking_number}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <CopyChip text={order.tracking_number} label="Number" />
                  {trackUrl && (
                    <a
                      href={trackUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700"
                    >
                      Track <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* What */}
      <div className="mt-3 animate-fade-up opacity-0 rounded-xl border border-slate-200 bg-white" style={delay(1)}>
        <p className="border-b border-slate-100 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          What you ordered ({items.length})
        </p>
        <ul className="divide-y divide-slate-100">
          {(items as any[]).map((it) => {
            const inner = (
              <>
                {it.image_url ? (
                  <img src={it.image_url} alt="" loading="lazy" className="h-12 w-12 shrink-0 rounded-lg border border-slate-200 object-cover" />
                ) : (
                  <div className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-slate-100 font-mono text-[10px] text-slate-500">
                    {(it.product_sku ?? "—").toString().slice(0, 4)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">{it.product_name}</p>
                  <p className="truncate text-xs text-slate-500">
                    {it.product_sku && <span className="font-mono">SKU {it.product_sku}</span>}
                    {it.oem && <span className="font-mono"> · OEM {it.oem}</span>}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-semibold text-slate-900">
                    {eur(it.line_total_eur ?? Number(it.unit_price_eur) * it.quantity)}
                  </p>
                  <p className="text-xs text-slate-500">{eur(it.unit_price_eur)} × {it.quantity}</p>
                </div>
              </>
            );
            const canReview =
              !!it.product_id &&
              ["paid", "started", "shipped", "delivered"].includes(String(order.status));
            return (
              <li key={it.id}>
                {it.product_slug ? (
                  <Link
                    to={"/p/$slug" as never}
                    params={{ slug: it.product_slug } as never}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-3 p-3 transition hover:bg-slate-50"
                  >
                    {inner}
                  </Link>
                ) : (
                  <div className="flex items-center gap-3 p-3">{inner}</div>
                )}
                {canReview ? (
                  <div className="-mt-1 px-3 pb-3">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setReviewTarget({
                          productId: it.product_id,
                          productName: it.product_name,
                          imageUrl: it.image_url,
                        });
                      }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-700 transition hover:bg-amber-100"
                    >
                      <Star className="h-3 w-3" /> Write a review
                    </button>
                  </div>
                ) : null}
              </li>
            );

          })}

        </ul>
        <div className="space-y-1 border-t border-slate-100 px-4 py-3 text-sm">
          <Row label="Subtotal" value={eur(order.subtotal_eur)} />
          <Row label="Shipping" value={eur(order.shipping_eur)} />
          {Number(order.tax_eur) > 0 && <Row label="VAT" value={eur(order.tax_eur)} />}
          <div className="my-1 h-px bg-slate-100" />
          <Row label="Total" value={eur(order.total_eur)} bold />
        </div>
      </div>

      {/* Where + When */}
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="animate-fade-up opacity-0 rounded-xl border border-slate-200 bg-white p-4" style={delay(2)}>
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <MapPin className="h-3.5 w-3.5 text-[#2563EB]" /> Shipping to
          </p>
          <p className="mt-2 text-sm font-semibold text-slate-900">{order.customer_name}</p>
          {order.company_name && <p className="text-sm text-slate-700">{order.company_name}</p>}
          <div className="mt-0.5 space-y-0.5 text-sm text-slate-600">
            {order.shipping_line1 && <p>{order.shipping_line1}</p>}
            {order.shipping_line2 && <p>{order.shipping_line2}</p>}
            {(order.shipping_postal_code || order.shipping_city) && (
              <p>{[order.shipping_postal_code, order.shipping_city].filter(Boolean).join(" ")}</p>
            )}
            {!order.shipping_line1 && order.shipping_address && <p className="whitespace-pre-line">{order.shipping_address}</p>}
            <p>{flag} {order.country}</p>
          </div>
          {order.notes && (
            <p className="mt-2 rounded-lg bg-slate-50 p-2 text-xs text-slate-700">{order.notes}</p>
          )}
        </div>

        <div className="animate-fade-up opacity-0 rounded-xl border border-slate-200 bg-white p-4" style={delay(3)}>
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <CalendarDays className="h-3.5 w-3.5 text-[#2563EB]" /> When it happened
          </p>
          <ul className="mt-2 space-y-2">
            <TimelineItem title="Order placed" ts={order.created_at} tone="bg-slate-100 text-slate-600" />
            {(history as any[]).map((h) => (
              <TimelineItem
                key={h.id}
                title={`${metaFor(h.from_status).label} → ${metaFor(h.to_status).label}`}
                ts={h.changed_at}
                tone={metaFor(h.to_status).tone}
              />
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 text-xs text-slate-500">
            <Hash className="h-3.5 w-3.5 text-slate-400" />
            <span className="font-mono">#{order.order_number}</span>
            <CopyChip text={String(order.order_number)} label="Copy" />
            <span className="ml-auto">{timeAgo(order.created_at)}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-3 flex animate-fade-up flex-wrap items-center gap-2 opacity-0" style={delay(4)}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            reorder();
          }}
          className="inline-flex items-center gap-1.5 rounded-xl bg-[#2563EB] px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-[#1d4ed8] active:scale-95"
        >
          <ShoppingBag className="h-3.5 w-3.5" /> Reorder
        </button>
      </div>

      {/* Support note */}
      <p className="mt-4 animate-fade-up text-center text-xs text-slate-500 opacity-0" style={delay(5)}>
        For any questions about this order, email us at{" "}
        <a
          href={`mailto:info@contact.fixhen.com?subject=${encodeURIComponent(`Order #${order.order_number}`)}`}
          onClick={(e) => e.stopPropagation()}
          className="font-semibold text-[#2563EB] underline underline-offset-2"
        >
          info@contact.fixhen.com
        </a>
      </p>

      {reviewTarget ? (
        <ReviewDialog
          productId={reviewTarget.productId}
          productName={reviewTarget.productName}
          imageUrl={reviewTarget.imageUrl}
          onClose={() => setReviewTarget(null)}
        />
      ) : null}
    </div>

  );
}

function TimelineItem({ title, ts, tone }: { title: string; ts: string; tone: string }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${tone.split(" ")[0]}`} />
      <div className="min-w-0">
        <p className="truncate text-slate-900">{title}</p>
        <p className="text-xs text-slate-500">{new Date(ts).toLocaleString()}</p>
      </div>
    </li>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? "font-semibold text-slate-900" : "text-slate-500"}>{label}</span>
      <span className={bold ? "font-bold text-slate-900" : "text-slate-700"}>{value}</span>
    </div>
  );
}
